import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAtom, useSetAtom } from 'jotai';

import { computeTier } from '@flintmint/core-shared/utils';
import type { UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import { Button } from '@flintmint/web-shared/components/button';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Modal } from '@flintmint/web-shared/components/modal';

import { subscribeAgentRunEvents } from '@/hooks/llm/use-llm-agent';
import { Logo } from '@/components/logo';
import { useModalLayer } from '@/components/full-screen-overlay/use-modal-layer';
import { energyActionLabels, useEnergyAction } from '@/features/llm-chat/energy/use-energy-action';
import { getEnergyView, isRenewalInGrace, isTrialEnergyUsedUp } from '@/features/llm-chat/energy/energy-view';
import { useUserAccount } from '@/hooks/use-user-account';
import { urlOverlaySearch } from '@/hooks/use-url-overlay';
import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { aiRefusalInvitationOpenAtom, graceAllowanceUsedUpPeriodAtom } from '@/state';

import { isFirstChargeRetrying, isTrialEligible } from '../billing-state';

import styles from './ai-refusal-invitation.module.scss';

/**
 * Mounted once over every screen. Invites a Free account whose AI Preview is used up to a Trial or a plan,
 * a Free account whose first charge failed at Trial expiry to pay it, a Trial whose Energy is used up to start
 * its plan now, and a renewal whose grace allowance is used up to update its payment method.
 */
export const AiRefusalInvitation = () => {
  const [open, setOpen] = useAtom(aiRefusalInvitationOpenAtom);
  const openInvitation = useOpenAiRefusalInvitation();
  const { account } = useUserAccount();
  const { runEnergyAction, opening } = useEnergyAction();
  const navigate = useNavigate();
  const action = getEnergyView(account)?.action;
  const close = () => setOpen(false);
  const visible = open && isInvited(account);
  const trialUsedUp = isTrialEnergyUsedUp(account);
  const firstChargeFailed = isFirstChargeRetrying(account?.stripeSubscription);
  const inGrace = isRenewalInGrace(account);

  // Every credits refusal invites, from any chat or from Generate Text.
  useEffect(
    () =>
      subscribeAgentRunEvents(({ event }) => {
        if ('refusal' in event && event.refusal) openInvitation();
      }),
    [openInvitation]
  );

  // The layer makes every body child but its overlay inert; the Modal's portal root is that body child.
  const contentRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    layerRef.current = contentRef.current?.closest<HTMLElement>('body > *') ?? null;
  });
  useModalLayer(layerRef, headingRef, close, visible);

  return (
    <Modal freeForm open={visible} maxWidth={600} holderClassName={styles.card} closeOnEscape={false} onClose={close}>
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-refusal-invitation-title"
        className={clsx(styles.content, { [styles.planNotice]: trialUsedUp || firstChargeFailed || inGrace })}
      >
        <div className={styles.logo}>
          <Logo />
        </div>
        <Divider orientation="horizontal" />
        {trialUsedUp ? (
          <>
            <div className={styles.heading}>
              <h1 id="ai-refusal-invitation-title" ref={headingRef} tabIndex={-1}>
                Your Trial Energy is used up
              </h1>
              <p>Start your chosen plan now for fresh Monthly Energy.</p>
            </div>
            <div className={styles.trial}>
              <h2>More AI, ready when you are</h2>
              <p>
                Your Trial is still active, but its Energy is used up. Starting your selected plan ends Trial and begins paid
                access now.
              </p>
            </div>
            <p className={styles.smallPrint}>
              Decide later keeps your scheduled start date. Cancelling stops the scheduled charge.
            </p>
          </>
        ) : firstChargeFailed ? (
          <>
            <div className={styles.heading}>
              <h1 id="ai-refusal-invitation-title" ref={headingRef} tabIndex={-1}>
                Your first payment didn’t go through
              </h1>
              <p>Your plan has not started. You’re on Free.</p>
            </div>
            <div className={styles.trial}>
              <h2>Pay or stop the pending plan</h2>
              <p>Your Trial has ended. We’ll retry the first payment automatically.</p>
              <p>Update your payment method to start your plan, or stop the pending plan from Subscription & Billing.</p>
            </div>
            <p className={styles.smallPrint}>
              Continuing on Free does not cancel the pending subscription or stop payment retries.
            </p>
          </>
        ) : inGrace ? (
          <>
            <div className={styles.heading}>
              <h1 id="ai-refusal-invitation-title" ref={headingRef} tabIndex={-1}>
                Update payment to continue with AI
              </h1>
              <p>You can still read and edit your work.</p>
            </div>
            <div className={styles.trial}>
              <h2>Your renewal payment is still unpaid</h2>
              <p>Update your payment method to restore Monthly Energy and access to your purchased Joules.</p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.heading}>
              <h1 id="ai-refusal-invitation-title" ref={headingRef} tabIndex={-1}>
                Keep going with AI
              </h1>
              <p>Your AI Preview is used up. Try more AI when you are ready.</p>
            </div>
            {isTrialEligible(account) && (
              <div className={styles.trial}>
                <h2>Explore a 30-day Trial</h2>
                <p>
                  Get a fresh, one-time Trial Energy allowance. Choose the paid plan you want afterward and add a payment method.
                </p>
                <p>Your chosen plan starts when Trial ends unless you cancel.</p>
              </div>
            )}
            <p className={styles.smallPrint}>You can keep working with manual notes, Blocks and existing content on Free.</p>
          </>
        )}
        <div className={styles.actions}>
          {action && (
            <Button
              size="big"
              fullWidth
              cornerRadius="smooth"
              disabled={opening}
              onClick={() => {
                close();
                runEnergyAction(action);
              }}
            >
              {energyActionLabels[action]}
            </Button>
          )}
          {trialUsedUp && (
            <Button
              size="big"
              fullWidth
              cornerRadius="smooth"
              appearance="clear"
              onClick={() => {
                close();
                void navigate({
                  pathname: appRoutes.settingsBilling,
                  search: urlOverlaySearch(urlOverlays.cancelSubscription),
                });
              }}
            >
              Cancel Trial
            </Button>
          )}
          {!firstChargeFailed && (
            <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" onClick={close}>
              {trialUsedUp ? 'Decide later' : inGrace ? 'Close' : 'Continue with Free'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

/**
 * Opens the invitation from any refusal; only a loaded Free account, a Trial with its Energy used up or a renewal in
 * its grace period queues it. In a renewal's grace period the only stop is the grace allowance, so the refusal also
 * marks it used up for the period. Stable while the admission and the period hold, so the run-event subscription is
 * not re-created every render.
 */
export const useOpenAiRefusalInvitation = () => {
  const setOpen = useSetAtom(aiRefusalInvitationOpenAtom);
  const setGraceAllowanceUsedUpPeriod = useSetAtom(graceAllowanceUsedUpPeriodAtom);
  const { account } = useUserAccount();
  const invited = isInvited(account);
  const gracePeriodStart = isRenewalInGrace(account) ? account?.stripeSubscription?.currentPeriodStart : undefined;

  return useCallback(() => {
    if (gracePeriodStart) setGraceAllowanceUsedUpPeriod(gracePeriodStart);
    if (invited) setOpen(true);
  }, [invited, gracePeriodStart, setOpen, setGraceAllowanceUsedUpPeriod]);
};

const isInvited = (account: UserAccountDoc | undefined) =>
  !!account && (computeTier(account) === 'free' || isTrialEnergyUsedUp(account) || isRenewalInGrace(account));
